/**
 * Task 4 - group similar pieces (same brand + category + similar name) into one stack, and keep similar colours
 * next to each other. Pure functions; the grid decides how to render.
 */

const COLOR_WORDS = {
  black: "#111111", jet: "#111111", charcoal: "#3a3a3a", graphite: "#444444", gray: "#9a9a9a", grey: "#9a9a9a", heather: "#a8a8a8", silver: "#c4c4c4",
  white: "#f5f5f5", ivory: "#f3eee2", cream: "#f1e6cf", ecru: "#e8dcc3", beige: "#d9c6a5", sand: "#d8c39b", stone: "#c9bfae", oatmeal: "#ddd2bd",
  tan: "#c89b6a", camel: "#c19a6b", khaki: "#b8a06a", taupe: "#9c8c7a", brown: "#6b4a2f", chocolate: "#4b2e1f", mocha: "#6e4b3a", rust: "#b7410e", cognac: "#9a4f22",
  navy: "#1f2a4d", indigo: "#2b3a67", blue: "#3b6fd1", denim: "#4a6fa5", wash: "#6d8fbf", sky: "#8ec5f2", cobalt: "#2a4fd6", teal: "#2a8f8a", turquoise: "#3fbfb8",
  green: "#3b8f4a", olive: "#6b7a3a", forest: "#2d5a3a", sage: "#9fb59a", mint: "#b6e3c8", lime: "#9ccc3c", emerald: "#2e9c6a",
  red: "#c6302c", burgundy: "#6e1f2e", maroon: "#6e1f2e", wine: "#722f3a", oxblood: "#5b1a1f", crimson: "#b4232f", cherry: "#b3222e",
  pink: "#e9a1b6", blush: "#e8c3c5", rose: "#d98a9a", fuchsia: "#d4348c", magenta: "#c8328f", coral: "#f08a74", peach: "#f5c3a4",
  orange: "#e5742a", tangerine: "#f08a2b", mustard: "#d5a021", yellow: "#f2cf3a", gold: "#c9a227", lemon: "#f4e36b",
  purple: "#6f4aa3", lavender: "#b9a6d9", lilac: "#c7b0e0", plum: "#5a2a4d", violet: "#7c4bc4", mauve: "#a98aa6",
  multi: "#999999", multicolor: "#999999", multicolour: "#999999", print: "#999999", pattern: "#999999",
};
const MODIFIERS = new Set(["light", "dark", "deep", "pale", "mid", "mid-wash", "washed", "faded", "bright", "soft", "muted", "dusty", "off", "off-white", "vintage", "heathered", "melange", "marl", "acid", "stone", "raw"]);
const SIZE_TOKENS = /^(xxs|xs|s|m|l|xl|xxl|2xl|3xl|xxxl|\d{1,2}(\.\d)?|w\d{2}|l\d{2}|us\s?\d+|eu\s?\d+|uk\s?\d+)$/i;
const NOISE = new Set(["the", "a", "an", "of", "in", "and", "&", "with", "for", "men's", "mens", "women's", "womens", "unisex"]);

export const colorHex = (name) => {
  const toks = String(name || "").toLowerCase().replace(/[^a-z\s-]/g, " ").split(/[\s/,-]+/).filter(Boolean);
  // prefer the last real colour word ("light wash blue" -> blue, "heather gray" -> gray)
  for (let i = toks.length - 1; i >= 0; i--) if (COLOR_WORDS[toks[i]] && !MODIFIERS.has(toks[i])) return shade(COLOR_WORDS[toks[i]], toks);
  for (const t of toks) if (COLOR_WORDS[t]) return COLOR_WORDS[t];
  return "#bdbdbd";
};

const shade = (hex, toks) => {
  const l = toks.includes("light") || toks.includes("pale") || toks.includes("faded") || toks.includes("wash") ? 0.18 : toks.includes("dark") || toks.includes("deep") ? -0.18 : 0;
  if (!l) return hex;
  const [h, s, li] = hexToHsl(hex);
  return hslToHex(h, s, Math.max(0, Math.min(1, li + l)));
};

export const hexToHsl = (hex) => {
  const n = parseInt(hex.replace("#", ""), 16);
  const r = ((n >> 16) & 255) / 255, g = ((n >> 8) & 255) / 255, b = (n & 255) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0; const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = (g - b) / d + (g < b ? 6 : 0); else if (max === g) h = (b - r) / d + 2; else h = (r - g) / d + 4;
    h /= 6;
  }
  return [h * 360, s, l];
};
const hslToHex = (h, s, l) => {
  const f = (n) => { const k = (n + h / 30) % 12; const a = s * Math.min(l, 1 - l); const c = l - a * Math.max(-1, Math.min(k - 3, 9 - k, 1)); return Math.round(255 * c).toString(16).padStart(2, "0"); };
  return `#${f(0)}${f(8)}${f(4)}`;
};

/** Base colour word for filtering ("light wash blue" -> "blue", "heather gray" -> "gray"); "" when unknown. */
export const colorFamily = (name) => {
  const toks = String(name || "").toLowerCase().replace(/[^a-z\s-]/g, " ").split(/[\s/,-]+/).filter(Boolean);
  for (let i = toks.length - 1; i >= 0; i--) if (COLOR_WORDS[toks[i]] && !MODIFIERS.has(toks[i])) return toks[i] === "grey" ? "gray" : toks[i];
  return toks[toks.length - 1] || "";
};

/** Sort key so similar colours sit next to each other: neutrals first (dark -> light), then around the hue wheel. */
export const colorSortKey = (name) => {
  const [h, s, l] = hexToHsl(colorHex(name));
  if (s < 0.12) return l;                 // neutrals 0..1
  return 2 + h / 360 + (1 - l) * 0.001;   // chromatic 2..3
};
export const byColor = (a, b) => colorSortKey(a.primary_color) - colorSortKey(b.primary_color) || (a.name || "").localeCompare(b.name || "");

/** Name tokens with brand, colours, sizes and noise removed - what's left is the "model". */
export const nameTokens = (item) => {
  const brand = String(item.brand || "").toLowerCase().split(/\s+/).filter(Boolean);
  const colours = new Set([...(item.colors || []), item.primary_color].filter(Boolean).flatMap((c) => String(c).toLowerCase().split(/[\s/,-]+/)));
  return String(item.name || "").toLowerCase().replace(/[^a-z0-9\s'-]/g, " ").split(/\s+/).filter(Boolean)
    .filter((t) => !brand.includes(t) && !colours.has(t) && !COLOR_WORDS[t] && !MODIFIERS.has(t) && !NOISE.has(t) && !SIZE_TOKENS.test(t));
};
const jaccard = (a, b) => { const A = new Set(a), B = new Set(b); const inter = [...A].filter((x) => B.has(x)).length; const uni = new Set([...A, ...B]).size; return uni ? inter / uni : 0; };

/**
 * Group a sorted list into entries: { kind: "item", item } | { kind: "stack", key, items, name, brand }.
 * A stack needs >= 2 pieces with the same brand + category and similar names (>= 0.6 token overlap).
 * Order of entries follows the first member; pieces inside a stack are ordered by colour.
 */
export const groupSimilar = (list) => {
  const entries = [];
  const used = new Set();
  const tokCache = new Map();
  const toks = (i) => { if (!tokCache.has(i.id)) tokCache.set(i.id, nameTokens(i)); return tokCache.get(i.id); };
  for (let i = 0; i < list.length; i++) {
    const a = list[i];
    if (used.has(a.id)) continue;
    const members = [a];
    if (a.brand && a.category) {
      for (let j = i + 1; j < list.length; j++) {
        const b = list[j];
        if (used.has(b.id) || !b.brand || b.category !== a.category) continue;
        if (String(b.brand).toLowerCase().trim() !== String(a.brand).toLowerCase().trim()) continue;
        const ta = toks(a), tb = toks(b);
        const contained = ta.length > 0 && tb.length > 0 && (ta.every((t) => tb.includes(t)) || tb.every((t) => ta.includes(t)));
        const same = (ta.length === 0 && tb.length === 0) || contained || jaccard(ta, tb) >= 0.6 || (a.subcategory && a.subcategory === b.subcategory && jaccard(ta, tb) >= 0.5);
        if (same) members.push(b);
      }
    }
    if (members.length >= 2) {
      members.forEach((m) => used.add(m.id));
      const sorted = members.slice().sort(byColor);
      const common = toks(a).join(" ");
      entries.push({ kind: "stack", key: `stack:${a.id}`, items: sorted, brand: a.brand, name: common ? titleCase(common) : (a.name || a.subtype || "Pieces") });
    } else {
      used.add(a.id);
      entries.push({ kind: "item", item: a });
    }
  }
  return entries;
};

const titleCase = (s) => s.replace(/\b\w/g, (c) => c.toUpperCase());
